import type { AuditEvent } from "../../api/types.js";
import { findingRecordHref } from "../../app/routing.js";

export const ENTITY_TYPES: readonly AuditEvent["entityType"][] = [
  "repository",
  "snapshot",
  "scan",
  "finding",
  "export",
  "auth",
  "threat_model",
];

export const ACTION_PRESETS = [
  "policy.decision",
  "finding.human_validated",
  "remediation.proposal_transitioned",
  "github.webhook_delivery_received",
  "scan.created",
  "threat_model.revision_appended",
] as const;

export type AuditActionTone = "positive" | "critical" | "info" | "warning" | "neutral";

export function actionFamily(action: string): string {
  const dot = action.indexOf(".");
  return dot > 0 ? action.slice(0, dot) : action;
}

export function auditActionTone(action: string): AuditActionTone {
  const lower = action.toLowerCase();
  if (/(reject|revoke|fail|unassign|delete|expire|rollback)/u.test(lower)) {
    return "critical";
  }
  if (/(approv|verified|processed|created|saved|validated|linked)/u.test(lower)) {
    return "positive";
  }
  if (lower.startsWith("remediation.") || lower.startsWith("policy.")) return "warning";
  if (lower.startsWith("github.") || lower.startsWith("export.") || lower.startsWith("auth.")) {
    return "info";
  }
  return "neutral";
}

export function auditActionLabel(action: string): string {
  return action.replaceAll(".", " ").replaceAll("_", " ");
}

export function auditEntityHref(event: Pick<AuditEvent, "entityType" | "entityId">): string | null {
  switch (event.entityType) {
    case "finding":
      return findingRecordHref(event.entityId);
    case "scan":
      return `/scans?scanId=${encodeURIComponent(event.entityId)}`;
    case "repository":
      return "/sources";
    case "threat_model":
      return "/threat-models";
    default:
      return null;
  }
}

export function auditDetailEntries(
  details: AuditEvent["details"],
): Array<{ key: string; value: string; href: string | null }> {
  return Object.entries(details)
    .filter(([, value]) => value !== null && value !== "")
    .map(([key, value]) => {
      const text = String(value);
      const looksLikeFinding = key.toLowerCase().includes("finding") && text.startsWith("finding_");
      return {
        key,
        value: text,
        href: looksLikeFinding ? findingRecordHref(text) : null,
      };
    });
}

export function auditHeadline(input: {
  eventCount: number;
  findingLinked: number;
  actorCount: number;
}): string {
  if (input.eventCount === 0) {
    return "No recorded events match these filters. Absence of a row is not proof the action never happened.";
  }
  if (input.findingLinked > 0) {
    return `${input.findingLinked} of ${input.eventCount} events on this page name a finding you can open.`;
  }
  return `${input.eventCount} immutable events from ${input.actorCount} actor${input.actorCount === 1 ? "" : "s"} on this page.`;
}
